import React, { useState, useEffect } from 'react';
import { DollarSign, Heart, Users, Briefcase, CheckCircle, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { sponsorshipTiers } from '../mockData';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GetInvolved = () => {
  const { toast } = useToast();
  const [donationAmount, setDonationAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [isPolling, setIsPolling] = useState(false);
  const [volunteerForm, setVolunteerForm] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [partnerForm, setPartnerForm] = useState({
    organization: '',
    contactName: '',
    email: '',
    phone: '',
    partnershipType: '',
    message: ''
  });

  // Poll payment status after returning from Stripe
  useEffect(() => {
    const pollPaymentStatus = async (sessionId, attempts = 0, isSponsorship = false) => {
      const maxAttempts = 5;
      
      if (attempts >= maxAttempts) {
        toast({
          title: 'Payment Status Check Timed Out',
          description: 'Please check your email for confirmation.',
          variant: 'destructive'
        });
        setIsPolling(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/donations/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          toast({
            title: isSponsorship ? 'Thank You for Your Sponsorship!' : 'Thank You for Your Donation!',
            description: `Your ${isSponsorship ? 'sponsorship' : 'donation'} of $${(response.data.amount_total / 100).toFixed(2)} was successful. You will receive a receipt via email.`,
          });
          // Remove session_id from URL
          window.history.replaceState({}, document.title, window.location.pathname);
          setIsPolling(false);
          return;
        } else if (response.data.status === 'expired') {
          toast({
            title: 'Payment Expired',
            description: 'Your payment session expired. Please try again.',
            variant: 'destructive'
          });
          window.history.replaceState({}, document.title, window.location.pathname);
          setIsPolling(false);
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1, isSponsorship), 2000);
      } catch (error) {
        console.error('Error checking payment status:', error);
        toast({
          title: 'Error',
          description: 'Failed to verify payment status.',
          variant: 'destructive'
        });
        setIsPolling(false);
      }
    };

    // Check if returning from Stripe
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentType = urlParams.get('type');
    
    if (sessionId && !isPolling) {
      setIsPolling(true);
      const isSponsorship = paymentType === 'sponsorship';
      toast({
        title: 'Verifying Payment',
        description: isSponsorship ? 'Please wait while we confirm your sponsorship...' : 'Please wait while we confirm your donation...',
      });
      pollPaymentStatus(sessionId, 0, isSponsorship);
    }
  }, [toast, isPolling]);

  const donationAmounts = ['25', '50', '100', '250', '500', '1000'];

  const handleSponsorshipCheckout = async (tier) => {
    try {
      const response = await axios.post(`${API}/sponsorships/create-checkout`, {
        tier: tier.toLowerCase(),
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Sponsorship checkout error:', error);
      toast({
        title: 'Error',
        description: 'Failed to process sponsorship. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleDonate = async (e) => {
    e.preventDefault();
    const amount = donationAmount === 'custom' ? customAmount : donationAmount;
    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid donation amount.',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await axios.post(`${API}/donations/create-checkout`, {
        package_id: donationAmount,
        custom_amount: donationAmount === 'custom' ? parseFloat(customAmount) : null,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Donation error:', error);
      toast({
        title: 'Error',
        description: 'Failed to process donation. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleVolunteerSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/volunteers/apply`, volunteerForm);
      toast({
        title: 'Application Received!',
        description: 'Thank you for your interest in volunteering. We\'ll contact you soon!',
      });
      setVolunteerForm({
        name: '',
        email: '',
        phone: '',
        interest: '',
        message: ''
      });
    } catch (error) {
      console.error('Volunteer application error:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit application. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/partnerships/inquire`, {
        organization: partnerForm.organization,
        contact_name: partnerForm.contactName,
        email: partnerForm.email,
        phone: partnerForm.phone,
        partnership_type: partnerForm.partnershipType,
        message: partnerForm.message
      });
      toast({
        title: 'Partnership Inquiry Received!',
        description: 'Thank you for your interest. Our team will reach out within 48 hours.',
      });
      setPartnerForm({
        organization: '',
        contactName: '',
        email: '',
        phone: '',
        partnershipType: '',
        message: ''
      });
    } catch (error) {
      console.error('Partnership inquiry error:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit inquiry. Please try again.',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933]/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Get <span className="text-[#E6B933]">Involved</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Your support helps us expand opportunities for underprivileged youth. Choose how you'd like to make an impact.
            </p>
          </div>
        </div>
      </section>

      {/* Involvement Options */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="donate" className="w-full">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-4 mb-12 h-auto">
              <TabsTrigger value="donate" className="py-4 data-[state=active]:bg-[#E6B933] data-[state=active]:text-white">
                <DollarSign className="mr-2" size={18} />
                Donate
              </TabsTrigger>
              <TabsTrigger value="volunteer" className="py-4 data-[state=active]:bg-[#E6B933] data-[state=active]:text-white">
                <Heart className="mr-2" size={18} />
                Volunteer
              </TabsTrigger>
              <TabsTrigger value="partner" className="py-4 data-[state=active]:bg-[#E6B933] data-[state=active]:text-white">
                <Briefcase className="mr-2" size={18} />
                Partner
              </TabsTrigger>
              <TabsTrigger value="sponsor" className="py-4 data-[state=active]:bg-[#E6B933] data-[state=active]:text-white">
                <Users className="mr-2" size={18} />
                Sponsor
              </TabsTrigger>
            </TabsList>

            {/* Donate Tab */}
            <TabsContent value="donate">
              <div className="max-w-2xl mx-auto">
                <Card className="p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Make a Donation</h2>
                  <p className="text-gray-600 mb-8">
                    Your contribution directly supports our programs and helps us serve more youth in need.
                  </p>
                  <form onSubmit={handleDonate}>
                    <div className="mb-6">
                      <Label className="text-base font-semibold mb-4 block">Select Amount</Label>
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {donationAmounts.map((amount) => (
                          <button
                            key={amount}
                            type="button"
                            onClick={() => {
                              setDonationAmount(amount);
                              setCustomAmount('');
                            }}
                            className={`py-4 px-4 rounded-lg border-2 font-semibold transition-all duration-300 ${
                              donationAmount === amount
                                ? 'border-[#E6B933] bg-[#E6B933]/10 text-[#E6B933]'
                                : 'border-gray-300 hover:border-[#E6B933] text-gray-700'
                            }`}
                          >
                            ${amount}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          onClick={() => setDonationAmount('custom')}
                          className={`py-4 px-6 rounded-lg border-2 font-semibold transition-all duration-300 ${
                            donationAmount === 'custom'
                              ? 'border-[#E6B933] bg-[#E6B933]/10 text-[#E6B933]'
                              : 'border-gray-300 hover:border-[#E6B933] text-gray-700'
                          }`}
                        >
                          Custom
                        </button>
                        {donationAmount === 'custom' && (
                          <Input
                            type="number"
                            placeholder="Enter amount"
                            value={customAmount}
                            onChange={(e) => setCustomAmount(e.target.value)}
                            className="flex-1"
                            min="1"
                          />
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-6 mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-gray-700 font-medium">Donation Amount:</span>
                        <span className="text-2xl font-bold text-gray-900">
                          ${donationAmount === 'custom' ? (customAmount || '0') : (donationAmount || '0')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        <CheckCircle className="inline mr-1" size={16} /> Tax-deductible contribution
                      </p>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-[#E6B933] hover:bg-[#D4A01F] text-white font-semibold py-6 text-lg transition-all duration-300 hover:scale-105"
                    >
                      Proceed to Payment
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                    <p className="text-xs text-gray-500 text-center mt-4">
                      Secure payment processing powered by Stripe
                    </p>
                  </form>
                </Card>
              </div>
            </TabsContent>

            {/* Volunteer Tab */}
            <TabsContent value="volunteer">
              <div className="max-w-2xl mx-auto">
                <Card className="p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Become a Volunteer</h2>
                  <p className="text-gray-600 mb-8">
                    Share your time, skills, and passion to make a direct impact on young lives.
                  </p>
                  <form onSubmit={handleVolunteerSubmit}>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="volunteer-name">Full Name *</Label>
                        <Input
                          id="volunteer-name"
                          required
                          value={volunteerForm.name}
                          onChange={(e) => setVolunteerForm({ ...volunteerForm, name: e.target.value })}
                          placeholder="Your name"
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="volunteer-email">Email *</Label>
                          <Input
                            id="volunteer-email"
                            type="email"
                            required
                            value={volunteerForm.email}
                            onChange={(e) => setVolunteerForm({ ...volunteerForm, email: e.target.value })}
                            placeholder="your@email.com"
                          />
                        </div>
                        <div>
                          <Label htmlFor="volunteer-phone">Phone</Label>
                          <Input
                            id="volunteer-phone"
                            type="tel"
                            value={volunteerForm.phone}
                            onChange={(e) => setVolunteerForm({ ...volunteerForm, phone: e.target.value })}
                            placeholder="(123) 456-7890"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="volunteer-interest">Area of Interest *</Label>
                        <Input
                          id="volunteer-interest"
                          required
                          value={volunteerForm.interest}
                          onChange={(e) => setVolunteerForm({ ...volunteerForm, interest: e.target.value })}
                          placeholder="Coaching, mentoring, tutoring, events, etc."
                        />
                      </div>
                      <div>
                        <Label htmlFor="volunteer-message">Tell Us About Yourself</Label>
                        <Textarea
                          id="volunteer-message"
                          rows={4}
                          value={volunteerForm.message}
                          onChange={(e) => setVolunteerForm({ ...volunteerForm, message: e.target.value })}
                          placeholder="Share your skills, experience, and why you want to volunteer..."
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full mt-6 bg-[#E6B933] hover:bg-[#D4A01F] text-white font-semibold py-6 text-lg transition-all duration-300"
                    >
                      Submit Application
                    </Button>
                  </form>
                </Card>
              </div>
            </TabsContent>

            {/* Partner Tab */}
            <TabsContent value="partner">
              <div className="max-w-2xl mx-auto">
                <Card className="p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Partner With Us</h2>
                  <p className="text-gray-600 mb-8">
                    Collaborate with WHY ME Foundation to create meaningful impact through strategic partnerships.
                  </p>
                  <form onSubmit={handlePartnerSubmit}>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="partner-org">Organization Name *</Label>
                        <Input
                          id="partner-org"
                          required
                          value={partnerForm.organization}
                          onChange={(e) => setPartnerForm({ ...partnerForm, organization: e.target.value })}
                          placeholder="Your organization"
                        />
                      </div>
                      <div>
                        <Label htmlFor="partner-contact">Contact Person *</Label>
                        <Input
                          id="partner-contact"
                          required
                          value={partnerForm.contactName}
                          onChange={(e) => setPartnerForm({ ...partnerForm, contactName: e.target.value })}
                          placeholder="Your name"
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="partner-email">Email *</Label>
                          <Input
                            id="partner-email"
                            type="email"
                            required
                            value={partnerForm.email}
                            onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })}
                            placeholder="your@email.com"
                          />
                        </div>
                        <div>
                          <Label htmlFor="partner-phone">Phone</Label>
                          <Input
                            id="partner-phone"
                            type="tel"
                            value={partnerForm.phone}
                            onChange={(e) => setPartnerForm({ ...partnerForm, phone: e.target.value })}
                            placeholder="(123) 456-7890"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="partner-type">Partnership Type *</Label>
                        <Input
                          id="partner-type"
                          required
                          value={partnerForm.partnershipType}
                          onChange={(e) => setPartnerForm({ ...partnerForm, partnershipType: e.target.value })}
                          placeholder="Corporate sponsor, facility access, mentorship, etc."
                        />
                      </div>
                      <div>
                        <Label htmlFor="partner-message">Partnership Details</Label>
                        <Textarea
                          id="partner-message"
                          rows={4}
                          value={partnerForm.message}
                          onChange={(e) => setPartnerForm({ ...partnerForm, message: e.target.value })}
                          placeholder="Tell us about your organization and how you'd like to partner..."
                        />
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full mt-6 bg-[#E6B933] hover:bg-[#D4A01F] text-white font-semibold py-6 text-lg transition-all duration-300"
                    >
                      Submit Inquiry
                    </Button>
                  </form>
                </Card>
              </div>
            </TabsContent>

            {/* Sponsor Tab */}
            <TabsContent value="sponsor">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Sponsorship Tiers</h2>
                  <p className="text-xl text-gray-600">
                    Choose a sponsorship level that aligns with your organization's impact goals
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  {sponsorshipTiers.map((tier, index) => (
                    <Card key={index} className={`p-6 hover:shadow-xl transition-all duration-300 border-t-4 ${
                      tier.tier === 'Platinum' ? 'border-[#E6B933]' : 'border-gray-300'
                    }`}>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.tier}</h3>
                      <p className="text-3xl font-bold text-[#E6B933] mb-6">{tier.amount}</p>
                      <ul className="space-y-3 mb-6">
                        {tier.benefits.map((benefit, bIndex) => (
                          <li key={bIndex} className="flex items-start space-x-3">
                            <CheckCircle className="text-[#E6B933] flex-shrink-0 mt-1" size={18} />
                            <span className="text-gray-600">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                      <Button 
                        className="w-full bg-[#E6B933] hover:bg-[#D4A01F] text-black font-semibold transition-all duration-300 hover:scale-105"
                        onClick={() => handleSponsorshipCheckout(tier.tier)}
                      >
                        Become a {tier.tier} Sponsor
                        <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </Card>
                  ))}
                </div>
                <p className="text-center text-gray-500 text-sm mt-6">
                  Secure payment processing powered by Stripe
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default GetInvolved;
